import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { FullPageSpinner } from '../components/common/FullPageSpinner';
import { AccessProvider, useAccess } from './AccessContext';
import { NoAccessPage } from './NoAccessPage';
import { ServiceErrorPage } from './ServiceErrorPage';

/**
 * Top-level auth + access gate (DOR adaptation of the template's `RootGate`).
 *
 * The ORDER of states is the whole point of the SPA-Auth Standard (rule 5) — never show
 * no-access while access is still resolving, and never show no-access for a transient/infra
 * failure:
 *
 *   - MSAL not ready                  → spinner
 *   - unauthenticated                 → /welcome (sign-in)
 *   - authenticated, resolving        → spinner            (token + /api/me in flight)
 *   - authenticated, transient error  → service-error + Retry   (NEVER no-access)
 *   - authenticated, not provisioned  → no-access (switch account + diagnostics)
 *   - authenticated + has profile     → the app
 *
 * Defence-in-depth: the API still authorises server-side; this gate only HIDES the app.
 */
export function RootGate() {
  const location = useLocation();
  const { isReady, isAuthenticated } = useAuth();

  // Wait for MSAL to finish any in-flight interaction (redirect handling) before deciding.
  if (!isReady) {
    return <FullPageSpinner label="Checking your session…" />;
  }

  // Not signed in → the branded login page, preserving the intended destination.
  if (!isAuthenticated) {
    const from = location.pathname + location.search + location.hash;
    return <Navigate to="/welcome" state={{ from }} replace />;
  }

  // Authenticated: resolve access (token + /api/me) inside the provider, then switch on it.
  return (
    <AccessProvider>
      <GateContent />
    </AccessProvider>
  );
}

/**
 * The authenticated-state switch. Exported for unit testing the gate-state ordering with a
 * mocked `useAccess` (rule 5: no-access is NEVER shown while loading or on a transient error).
 */
export function GateContent() {
  const { status } = useAccess();

  if (status === 'loading') {
    return <FullPageSpinner label="Loading your workspace…" />;
  }
  if (status === 'error') {
    return <ServiceErrorPage />;
  }
  if (status === 'noAccess') {
    return <NoAccessPage />;
  }
  return <Outlet />;
}
